<template>
  <q-dialog
    v-model="shown"
    :maximized="$q.screen.lt.sm"
  >
    <q-card
      :style="$q.screen.lt.sm ? '' : 'width: 600px;'"
    >
      <q-form
        ref="formRef"
      >
        <q-card-section class="row items-center q-mx-xs">
          <div
            class="text-h6 text-center"
          >
            {{ $t('pages.manageFriends.addFriendsAndFamilyHeading') }}
          </div>
          <q-space />
          <q-btn
            icon="close"
            flat
            round
            dense
            @click="close"
          />
        </q-card-section>
        <q-card-section>
          <q-banner
            class="bg-info q-mb-md row items-center"
            :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
          >
            <template #avatar>
              <q-icon
                name="info"
                :color="$isColorDark(currentSite.infoColor) ? 'white' : 'black'"
              />
            </template>
            {{ $t('pages.manageFriends.addFriendsAndFamilyBanner') }}
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-banner class="bg-secondary text-white q-mb-md row items-center">
            <template #avatar>
              <q-icon
                :name="bannerIcon"
                color="white"
              />
            </template>
            <template v-if="mode === AUTHORIZATION_MODE.EMAIL">
              {{ $t('pages.manageFriends.emailInviteInstructions') }}
            </template>
            <template v-if="mode === AUTHORIZATION_MODE.CARD">
              {{ $t('pages.manageFriends.cardInstructions') }}
            </template>
            <template v-if="mode === AUTHORIZATION_MODE.LINK">
              {{ $t('pages.manageFriends.linkInstructions') }}
            </template>
          </q-banner>
        </q-card-section>
        <q-card-section v-if="mode === AUTHORIZATION_MODE.EMAIL">
          <q-input
            v-model.trim="emailInput"
            :label="$t('form.labels.emailAddress')"
            type="email"
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
              val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
            ]"
            lazy-rules
            :autofocus="!emailInput"
            bottom-slots
            data-test-id="email-input"
          />
        </q-card-section>
        <q-card-section
          v-else-if="mode === AUTHORIZATION_MODE.CARD"
          :class="{ 'row no-wrap q-col-gutter-md': $q.screen.gt.xs }"
        >
          <q-input
            v-model.trim="cardInput"
            class="col-8"
            :label="$t('card.number')"
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
            ]"
            lazy-rules
            :autofocus="!cardInput"
            bottom-slots
          />
          <q-input
            v-model.trim="lastNameInput"
            class="col-4"
            :label="$t('form.labels.lastName')"
            :rules="[
              val => required.$validator(val) || $t('form.errors.required'),
            ]"
            lazy-rules
            bottom-slots
          />
        </q-card-section>
        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            class="col-auto"
            color="primary"
            :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
            size="lg"
            :label="buttonLabel"
            @click="addFriend"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { copyToClipboard } from 'quasar'
import AuthorizedPurchaser from 'src/models/authorized-purchaser/AuthorizedPurchaser.js'
import User from 'src/models/user/User'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'

export const AUTHORIZATION_MODE = {
  LINK: 'link',
  EMAIL: 'email',
  CARD: 'card'
}

export default {
  name: 'AuthorizedPurchaserDialog',
  mixins: [FormValidationMixin],
  props: {
    user: {
      type: User,
      required: false,
      default: null
    }
  },
  data () {
    return {
      shown: false,
      mode: AUTHORIZATION_MODE.LINK,
      emailInput: '',
      cardInput: '',
      lastNameInput: ''
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite'],
  computed: {
    activeUser () {
      return this.user || this.currentUser
    },
    buttonLabel () {
      if (this.mode === AUTHORIZATION_MODE.LINK) {
        return this.$i18n.t('pages.manageFriends.actions.copyLinkToClipboard')
      } else if (this.mode === AUTHORIZATION_MODE.EMAIL) {
        return this.$i18n.t('pages.manageFriends.actions.sendEmail')
      } else if (this.mode === AUTHORIZATION_MODE.CARD) {
        return this.$i18n.t('pages.manageFriends.actions.linkAccount')
      } else {
        throw new Error('Unhandled Condition')
      }
    },
    bannerIcon () {
      if (this.mode === AUTHORIZATION_MODE.LINK) {
        return AUTHORIZATION_MODE.LINK
      } else if (this.mode === AUTHORIZATION_MODE.EMAIL) {
        return 'email'
      } else if (this.mode === AUTHORIZATION_MODE.CARD) {
        return 'card_membership'
      } else {
        throw new Error('Unhandled Condition')
      }
    }
  },
  created () {
    _.extend(this, { email, required, AUTHORIZATION_MODE })
  },
  methods: {
    show (mode) {
      this.mode = mode
      this.shown = true
    },
    close () {
      this.shown = false

      this.emailInput = ''
      this.cardInput = ''
      this.lastNameInput = ''
    },
    addFriend () {
      this.validateForm(async () => {
        const authorizedPurchaser = new AuthorizedPurchaser({
          purchaserId: this.activeUser.id,
          email: this.emailInput,
          cardId: this.cardInput,
          lastName: this.lastNameInput
        })

        if (this.mode === AUTHORIZATION_MODE.LINK) {
          const { errors, link } = await authorizedPurchaser.linkInvite()

          if (errors.length) {
            this.$mh.showErrors(errors)
            return
          } else {
            copyToClipboard(link).then(() => {
              this.$mh.notify(this.$i18n.t('pages.manageFriends.actions.copiedInviteToClipboard'))
            }).catch(() => {
              this.$mh.notify(this.$i18n.t('pages.manageFriends.errors.unableToCopyInviteToClipboard'))

              // Display the invitation to the user if we have it
              if (link) {
                this.$mh.notify(link)
              }
            })
          }
        } else if (this.mode === AUTHORIZATION_MODE.EMAIL) {
          const errors = await authorizedPurchaser.emailInvite()

          if (errors.length) {
            this.$mh.showErrors(errors)
            return
          } else {
            this.$mh.notify(this.$i18n.t('pages.manageFriends.actions.sentFriendRequest'))
          }
        } else if (this.mode === AUTHORIZATION_MODE.CARD) {
          const errors = await authorizedPurchaser.addByCard()

          if (errors.length) {
            this.$mh.showErrors(errors)
            return
          } else {
            await this.activeUser.fetch(true)
            this.$mh.notify(this.$i18n.t('pages.manageFriends.actions.addedFriendByCard'))
          }
        }

        this.close()
      })
    }
  }
}
</script>
